import { useCallback, useRef } from 'react';
import { createBlock } from '../../../utils/blockUtils';
import { focusEditable } from '../../../utils/focusUtils';
import { isBlockEmpty } from '../../../utils/blockTreeHelpers';

// Only treat as "click empty canvas → focus/create end block" when the
// press ALSO started on empty canvas. Drag-selecting text across blocks
// retargets `click` onto `.doc-editor-blocks` (LCA); without this guard
// that focuses the last block and scroll-snaps the viewport to the bottom.
const isEmptyCanvasTarget = (el) =>
  !!el?.classList?.contains('doc-editor-blocks') ||
  !!el?.classList?.contains('bottom-drop-space');

export default function useCanvasHandlers({ blocksRef, commit }) {
  const canvasPressRef = useRef(null);

  const handleCanvasMouseDown = useCallback((e) => {
    canvasPressRef.current = isEmptyCanvasTarget(e.target)
      ? { x: e.clientX, y: e.clientY }
      : null;
  }, []);

  const handleCanvasClick = useCallback(
    (e) => {
      if (!isEmptyCanvasTarget(e.target)) return;

      const press = canvasPressRef.current;
      canvasPressRef.current = null;
      if (!press) return;
      if (Math.abs(e.clientX - press.x) > 5 || Math.abs(e.clientY - press.y) > 5) return;

      const current = blocksRef.current;
      const last = current[current.length - 1];
      if (last && last.type === 'text' && isBlockEmpty(last)) {
        focusEditable(last.id, { preventScroll: true });
      } else {
        const fresh = createBlock('text');
        commit([...current, fresh]);
        focusEditable(fresh.id, { preventScroll: true });
      }
    },
    [commit, blocksRef]
  );

  return { handleCanvasMouseDown, handleCanvasClick };
}
